/**
 * @graphql agg
 */

import { useCallback } from 'react';
import { graphql } from 'react-relay';
import { type FetchResult } from 'apollo-link';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';

import type { useUpdateCommentInCacheFragment$data } from './__generated__/useUpdateCommentInCacheFragment.graphql';

// Aliased types (for easier reading/passing around):
export type CachedCommentFragment = useUpdateCommentInCacheFragment$data;
export type CachedCommentReply = NonNullable<NonNullable<CachedCommentFragment>['replies']>[number];

// Unfortunately our Relay-friendly type generation tools don't support GQL fragments yet - but in
// order to keep the definition of the comment fields in sync (so the cache update fragment is
// always in sync with the full query) we will need to manually make any changes to one definition
// in any others as well, until our tooling supports fragments and then we move to a more
// fragmented-based approach.  Note there are also comment queries or fragments in the following
// files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data-manager/src/useCommentById.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineReplyMutation.ts
// - next/packages/object-comments/src/components/ObjectCommentContainer.tsx

// TODO: Replace with the exported fragment from useAllCommentsData.ts when our tooling supports
// it, if we are allowed to share fragments between packages/files.  If we are not allowed to share
// fragments, we should move this function - or at least the cache updating parts for the function
// - into the useAllCommentsData.ts file.
const updateCommentInCacheFragment = graphql`
	fragment useUpdateCommentInCacheFragment on Comment {
		id
		author {
			__typename
			displayName
			permissionType
			... on KnownUser {
				accountId
				permissionType
			}
			profilePicture {
				path
			}
			type
		}
		body(representation: ATLAS_DOC_FORMAT) {
			value
		}
		container {
			id
		}
		contentStatus
		createdAt {
			value
		}
		createdAtNonLocalized
		links {
			editui
			webui
		}
		location {
			__typename
			type
			... on FooterComment {
				commentResolveProperties {
					resolved
					resolvedFriendlyDate
					resolvedTime
					resolvedUser
				}
			}
			... on InlineComment {
				inlineMarkerRef
				inlineResolveProperties {
					isDangling
					resolved
					resolvedFriendlyDate
					resolvedTime
					resolvedUser
				}
				inlineText
			}
		}
		parentId
		permissions {
			isEditable
			isRemovable
			isResolvable
		}
		# reactionsSummary is currently not supported in this fragment because the Relay-friendly
		# interop functions do not yet support nested arguments in fragments. We will have to either
		# uncomment this (and pass the contentId in as a variable) when that is supported, or create a
		# separate function and fragment to update just reactions in cache (so the contentId would be
		# a first-level argument to that function and not a nested one).
		#
		# reactionsSummary(pageId: $contentId, contentType: "page", childType: "comment") {
		# 	ari
		# 	containerAri
		# 	reactionsCount
		# 	reactionsSummaryForEmoji {
		# 		id
		# 		count
		# 		emojiId
		# 		reacted
		# 	}
		# }
		replies {
			id
			author {
				__typename
				displayName
				permissionType
				... on KnownUser {
					accountId
				}
				profilePicture {
					path
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				editui
				webui
			}
			location {
				__typename
				type
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on InlineComment {
					inlineMarkerRef
					inlineResolveProperties {
						isDangling
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
					inlineText
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			# reactionsSummary is currently not supported in this fragment because the Relay-friendly
			# interop functions do not yet support nested arguments in fragments. See the comment above
			# for more details.
			#
			# reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
			# 	ari
			# 	containerAri
			# 	reactionsCount
			# 	reactionsSummaryForEmoji {
			# 		id
			# 		count
			# 		emojiId
			# 		reacted
			# 	}
			# }
			version {
				by {
					__typename
					displayName
				}
				number
				when
			}
		}
		version {
			by {
				__typename
				type
				displayName
				... on KnownUser {
					accountId
				}
			}
			friendlyWhen
			number
			when
		}
	}
`;

export type UpdateCommentCallback<TData> = (
	comment: CachedCommentFragment,
	mutationResult: FetchResult<TData>,
) => CachedCommentFragment;

type UpdateCommentInCacheProps<TData> = {
	mutationResult: FetchResult<TData>;
	commentId: string;
	updateCallback: UpdateCommentCallback<TData>;
	onError?: (error: Error) => void;
};

/**
 * This hook returns a function that can be used to perform updates to a
 * single comment in the Apollo GraphQL cache after a mutation that modifies
 * the comment has successfully completed. This includes mutations that
 * update the resolved state, edit the comment body, or add/remove reactions,
 * etc.. Adding or deleting comments - including replies - will be handled by
 * other cache updates functions.
 *
 * @returns function to update comment in cache
 */
export const useUpdateCommentInCache = <TData>() => {
	const readFragment = useReadFragmentInterop<useUpdateCommentInCacheFragment$data>({
		fragmentSpec: updateCommentInCacheFragment,
	});

	const writeFragment = useWriteFragmentInterop<
		Omit<useUpdateCommentInCacheFragment$data, ' $fragmentType'>
	>({
		fragmentSpec: updateCommentInCacheFragment,
	});

	/**
	 * Function to update a comment in the Apollo cache.
	 * @param mutationResult - The result of the mutation that triggered the cache update
	 * @param commentId - ID of the comment to update
	 * @param updateCallback - Callback function that receives the current comment data
	 *                         and returns the updated comment data
	 * @param onError - Optional error callback to handle errors during the update process
	 */
	const updateCommentInCache = useCallback(
		({ mutationResult, commentId, updateCallback, onError }: UpdateCommentInCacheProps<TData>) => {
			if (!commentId) {
				onError?.(new Error('Cannot update comment in cache: comment ID is missing'));
				return;
			}

			try {
				// The fragment ID format for Apollo cache
				const fragmentId = `Comment:${commentId}`;

				// Read the current comment from cache
				const cachedComment = readFragment({ id: fragmentId });

				if (!cachedComment) {
					// (There is a slight chance the comment is not in cache - but the mutation that calls
					// this function would have to completed successfully to call us, so presumably the
					// comment existed then. Thus the only legit reason for it to be missing from the cache
					// now is if another user deleted the comment in between the mutation and this cache
					// update call. That is unlikely, so let's log this as an error and return. If in
					// the future we find the legit use case is happening more often, we can change to a
					// warning instead.)
					onError?.(
						new Error(
							`Cannot update comment in cache: comment with ID ${commentId} not found in cache`,
						),
					);
					return;
				}

				// Apply the update callback on a copy of the cached comment
				// (Note that the updateCallback should return a new object, not mutate the original - so
				//  make copies of any nested objects you need to change also)
				const updatedComment = updateCallback({ ...cachedComment }, mutationResult);

				// Write the updated comment back to cache
				writeFragment({
					fragmentId,
					data: updatedComment,
				});
			} catch (err) {
				onError?.(new Error(`An error occurred when updating cached comment data: ${err}`));
				return;
			}
		},
		[readFragment, writeFragment],
	);

	return { updateCommentInCache };
};

// Utility updateCallback functions that can be shared by consumers

// Create a type alias for resolveInlineComment mutation rather than use a generated one so we
// aren't dependent a specific type file - even though this is copied from one of the Apollo-
// generated type files.  This avoids a dependency loop between packages. Note the "| undefined" on
// some variables is needed to be compatible with Relay-generated type files.
type ResolveProperties = {
	__typename?: 'InlineCommentResolveProperties';
	resolved: boolean;
	resolvedUser: string | null | undefined;
	isDangling: boolean;
	resolvedFriendlyDate: string | null | undefined;
	resolvedTime: number | null | undefined;
};

export type ResolveInlineCommentMutationResultData = {
	__typename?: 'Mutation';
	resolveInlineComment:
		| {
				__typename?: 'ResolveInlineCommentPayload';
				status: boolean;
				resolveProperties: ResolveProperties | null | undefined;
		  }
		| null
		| undefined;
};

// Create a type aliases for confluence_resolveComments mutation's response for an individual
// comment rather than use a generated ones so we aren't dependent a specific type file - even
// though these are mostly copied from one of the Apollo-generated type files.  This avoids a
// dependency loop between packages. Note the "| undefined" on some variables is needed to be
// compatible with Relay-generated type files, and that the "Partial<>" on the resolveProperties is
// needed because some of the existing mutations only request some of the properties in the
// mutation response.
type ResolveCommentsMutationResultData_commentResolutionStates =
	| {
			__typename?: 'ConfluenceCommentResolutionState';
			commentId: string;
			status: boolean | null | undefined;
			resolveProperties: Partial<ResolveProperties> | null | undefined;
	  }
	| null
	| undefined;

export type ResolveCommentsMutationResultData = {
	__typename?: 'Mutation';
	confluence_resolveComments:
		| {
				__typename?: 'ConfluenceResolveCommentsPayload';
				success: boolean;
				commentResolutionStates:
					| ReadonlyArray<
							ResolveCommentsMutationResultData_commentResolutionStates | undefined | null
					  >
					| undefined
					| null;
				// ignoring errors since we are not using them for cache updating and their type defintion is quite ugly
		  }
		| null
		| undefined;
};

// Create a type aliases for confluence_reopenComment mutation's response for an individual
// comment rather than use a generated ones so we aren't dependent a specific type file - even
// though these are mostly copied from one of the Apollo-generated type files.  This avoids a
// dependency loop between packages. Note the "| undefined" on some variables is needed to be
// compatible with Relay-generated type files.
type ReopenCommentMutationResultData_commentResolutionStates =
	| {
			__typename?: 'ConfluenceCommentResolutionState';
			commentId: string;
			status: boolean | null | undefined;
			resolveProperties?: Partial<ResolveProperties> | null;
	  }
	| null
	| undefined;

export type ReopenCommentMutationResultData = {
	__typename?: 'Mutation';
	confluence_reopenComment:
		| {
				__typename?: 'ConfluenceReopenCommentPayload';
				success: boolean;
				commentResolutionStates:
					| ReopenCommentMutationResultData_commentResolutionStates
					| undefined
					| null;
				// ignoring errors since we are not using them for cache updating and their type defintion is quite ugly
		  }
		| null
		| undefined;
};

type ResolveCommentInCacheProps = {
	comment: CachedCommentFragment;
	mutationStatus: boolean | undefined;
	resolveProperties: ResolveProperties | null | undefined;
};

/**
 * Marks an comment fragment as resolved or reopened, based on the result of the passed
 * resolvedProperties and mutationStatus props from different mutation results
 * Note this function is only intended to be used by other updateCallback functions that wrap it.
 * @param comment - the current cached inline comment fragment
 * @param mutationStatus - the resolved status from the mutation result
 * @param resolveProperties - the resolved properties from the mutation result.  Note that this
 * 													  must contain *all* non-optional properties so we can safely spread
 * 													  it into the comment location resolve properties.
 * @returns - the updated comment fragment, to be written back to the cache
 */
export const resolveCommentInCache = ({
	comment,
	mutationStatus,
	resolveProperties,
}: ResolveCommentInCacheProps): CachedCommentFragment => {
	if (!mutationStatus || !resolveProperties) {
		// Mutation result is missing or incomplete - so just return the comment unchanged
		return comment;
	}

	// Extract isDangling separately since it is handled differently, and exclude __typename since we
	// don't need it and Typescript would complain about it being included in the spread below.
	const { __typename, isDangling, ...otherResolveProperties } = resolveProperties;

	// Mark the comment as resolved in the cache, but how to do that depends on the comment type
	const location = comment.location;
	const newLocation = {
		...location,
		...('commentResolveProperties' in location
			? {
					commentResolveProperties: {
						...location.commentResolveProperties,
						...otherResolveProperties,
					},
				}
			: {}),
		...('inlineResolveProperties' in location
			? {
					inlineResolveProperties: {
						...location.inlineResolveProperties,
						...otherResolveProperties,
						isDangling,
					},
				}
			: {}),
		// Else location type is missing or unknown - so effectively return the location and comment unchanged
	};

	return { ...comment, location: newLocation };
};

// Create a type aliases for updateComment mutation's response for a comment rather than use a
// generated ones so we aren't dependent a specific type file - even though these are mostly copied
// from one of the Apollo-generated type files.  This avoids a dependency loop between packages.
type updateCommentsMutationResultData = {
	__typename?: 'Mutation';
	updateComment: {
		__typename?: 'Comment';
		id: string;
		body?: {
			__typename?: 'DocumentBody';
			value: string;
		};
		version?: {
			__typename?: 'Version';
			number: number | null;
			when: string | null;
			by: {
				__typename?: string;
				displayName: string | null;
			} | null;
		};
	};
};

export const updateCommentBodyInCacheCallback: UpdateCommentCallback<
	updateCommentsMutationResultData
> = (comment, mutationResult) => {
	const newBodyValue = mutationResult?.data?.updateComment.body?.value;
	const updatedBody = {
		...comment.body,
		// Remember that an empty string is a valid body value, so only skip updating if newBodyValue
		// is null or undefined
		...(newBodyValue || newBodyValue === '' ? { value: newBodyValue } : {}),
	};

	const newVersion = mutationResult?.data?.updateComment.version;
	const newDisplayName = newVersion?.by?.displayName;

	// If the existing comment's version doesn't have a "by" field, we don't want to add just part
	// of it - so set it to undefined so it won't be included in the updated comment. This probably
	// won't actually ever happen, but it makes Typescript happy just in case.
	const updatedBy = comment.version?.by
		? {
				...{ ...comment.version?.by },
				...(newDisplayName ? { displayName: newDisplayName } : {}),
			}
		: undefined;

	const updatedVersion = {
		...comment.version,
		...(newVersion?.number ? { number: newVersion.number } : {}),
		...(newVersion?.when ? { when: newVersion.when } : {}),
		...(updatedBy ? { by: { ...updatedBy } } : {}),
	};

	return {
		...comment,
		body: updatedBody,
		version: { ...updatedVersion },
	};
};
